import { ApiResult } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import { withApiResponse } from "common/utils/rtk-query.util";
import { List } from "common/models/list.model";
import { processingHubConfig } from "../config/processing-hub.config";
import {
  Document,
  ProcessingAssignment,
  SearchDocumentsRequest,
  UploadRequest,
  MultiUploadRequest,
  BatchUploadRequest,
  UploadResponse,
  DocumentActionRequest,
  DocumentListResponse,
  ProcessingAssignmentListResponse,
  PaginatedDocumentResponse,
  FlowConfiguration,
  FlowConfigurationResponse,
  ConfigurationDetails,
  GdxDocumentDetail,
  DocumentTypeInformation,
  BatchCreateRequest,
  BatchCreateResponse,
  BatchPaginatedResponse
} from "../models/processing-hub.model";
import Axios from "common/services/http-interceptor";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
export class ProcessingHubService extends HttpClient {
  public static getDocuments(searchParams: SearchDocumentsRequest) {
    const config = processingHubConfig.getDocuments(searchParams);
    return withApiResponse<DocumentListResponse>(async () => {
      const response = await this.invokeApi<DocumentListResponse>(config);
      return new ApiResult<DocumentListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async exportExtractedDataExcel(
    documentId: string | number
  ): Promise<ApiResult<Blob>> {
    const config = processingHubConfig.exportExtractedDataExcel(documentId);
    try {
      // Get authentication token
      const request = {
        scopes: [process.env.REACT_APP_API_SCOPE || ""]
      };
      // let tokenResult;
      // try {
      //   tokenResult = await msalInstance.acquireTokenSilent(request);
      // } catch (error: any) {
      //   if (error instanceof InteractionRequiredAuthError) {
      //     tokenResult = await msalInstance.acquireTokenPopup(request);
      //   } else {
      //     throw error;
      //   }
      // }

      // Make the request with blob response type
      const response = await Axios.post(config.url, config.data, {
        headers: {
          // Authorization: `Bearer ${tokenResult.accessToken}`,
          "Content-Type": "application/json"
        },
        responseType: "blob"
      });

      return new ApiResult<Blob>(response.data, response.status);
    } catch (error) {
      console.error("Error exporting excel:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return new ApiResult<Blob>(new Blob() as any, 500, errorMessage);
    }
  }

  public static getUploadedDocuments(
    hasDuplicate = false,
    paginationParams?: {
      page?: number;
      size?: number;
      sort?: string;
      dir?: string;
      search?: string;
      showDuplicates?: boolean;
      useEngApi?: boolean;
      flowId?: number | string;
    }
  ) {
    const config = processingHubConfig.getUploadedDocuments(
      hasDuplicate,
      paginationParams
    );
    return withApiResponse<PaginatedDocumentResponse>(async () => {
      const response = await this.invokeApi<PaginatedDocumentResponse>(config);
      return new ApiResult<PaginatedDocumentResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Keep a simple version for backward compatibility
  public static getUploadedDocumentsSimple(hasDuplicate = false) {
    const config = processingHubConfig.getUploadedDocuments(hasDuplicate);
    return withApiResponse<Document[]>(async () => {
      const response = await this.invokeApi<Document[]>(config);
      return new ApiResult<Document[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static uploadDocuments(uploadRequest: UploadRequest) {
    const config = processingHubConfig.uploadDocuments(uploadRequest);
    return withApiResponse<UploadResponse>(async () => {
      // Check if the data is FormData to set appropriate headers
      const headers =
        uploadRequest.files instanceof FormData
          ? {} // Let browser set Content-Type automatically for FormData
          : { "Content-Type": "application/json" };

      const response = await this.invokeApi<UploadResponse>(config, headers);
      return new ApiResult<UploadResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static multiUploadDocuments(uploadRequest: MultiUploadRequest) {
    const config = processingHubConfig.multiUploadDocuments(uploadRequest);
    return withApiResponse<UploadResponse>(async () => {
      // Check if the data is FormData to set appropriate headers
      const headers =
        uploadRequest.files instanceof FormData
          ? {} // Let browser set Content-Type automatically for FormData
          : { "Content-Type": "application/json" };

      const response = await this.invokeApi<UploadResponse>(config, headers);
      return new ApiResult<UploadResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getProcessingAssignments(documentType?: string) {
    const config = processingHubConfig.getProcessingAssignments(documentType);
    return withApiResponse<ProcessingAssignmentListResponse>(async () => {
      const response = await this.invokeApi<ProcessingAssignmentListResponse>(
        config
      );
      return new ApiResult<ProcessingAssignmentListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteDocument(documentId: string | number) {
    const config = processingHubConfig.deleteDocument(documentId);
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static async deleteEngineeringDocument(documentId: string | number) {
    const config = processingHubConfig.deleteEngineeringDocument(documentId);
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static async deleteGDXDocument(
    documentId: string | number,
    flowId: string | number
  ) {
    const config = processingHubConfig.deleteGDXDocument(documentId, flowId);
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static processDocument(actionRequest: DocumentActionRequest) {
    const config = processingHubConfig.processDocument(actionRequest);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static refreshDocuments() {
    const config = processingHubConfig.refreshDocuments();
    return withApiResponse<DocumentListResponse>(async () => {
      const response = await this.invokeApi<DocumentListResponse>(config);
      return new ApiResult<DocumentListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getGdxDocuments() {
    const config = processingHubConfig.getGdxDocuments();
    return withApiResponse<Document[]>(async () => {
      const response = await this.invokeApi<Document[]>(config);
      return new ApiResult<Document[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getGdxDocumentsPaginated(paginationParams: {
    page: number;
    flowConfigId: string | number;
    size: number;
    sort?: string;
    dir?: string;
    search?: string;
    showDuplicates?: boolean;
    batch_id?: string;
    validation_status?: string;
  }) {
    const config = processingHubConfig.getGdxDocumentsPaginated();

    const requestBody = {
      page: paginationParams.page,
      size: paginationParams.size,
      sort: paginationParams.sort || "",
      dir: paginationParams.dir || "",
      search: paginationParams.search || "",
      showDuplicates: paginationParams.showDuplicates || false,
      flow_id: paginationParams.flowConfigId,
      validation_status: paginationParams.validation_status || ""
    };

    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>({
        ...config,
        data: requestBody
      });

      // Transform API response to match GdxPaginatedResponse interface
      const transformedResponse = {
        data: response.value?.items || [],
        totalRecords: response.value?.total || 0,
        totalPages: response.value?.totalPages || 1,
        currentPage: response.value?.page || 1,
        pageSize: response.value?.size || 10,
        hasNext:
          (response.value?.page || 1) < (response.value?.totalPages || 1),
        hasPrevious: (response.value?.page || 1) > 1
      };

      return new ApiResult<any>(
        transformedResponse,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getFlowConfigurations() {
    const config = processingHubConfig.getFlowConfigurations();
    return withApiResponse<FlowConfiguration[]>(async () => {
      const response = await this.invokeApi<FlowConfiguration[]>(config);
      return new ApiResult<FlowConfiguration[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getFlowConfigurationById(configId: string | number) {
    const config = processingHubConfig.getFlowConfigurationById(configId);
    return withApiResponse<FlowConfiguration>(async () => {
      const response = await this.invokeApi<FlowConfiguration>(config);
      return new ApiResult<FlowConfiguration>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getConfigurationDetails(configId: string | number) {
    const config = processingHubConfig.getConfigurationDetails(configId);
    return withApiResponse<ConfigurationDetails>(async () => {
      const response = await this.invokeApi<ConfigurationDetails>(config);
      return new ApiResult<ConfigurationDetails>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static batchUpload(
    uploadRequest: BatchUploadRequest,
    userEmail?: string
  ) {
    return withApiResponse<UploadResponse>(async () => {
      const config = processingHubConfig.batchUpload(uploadRequest);

      // Set headers including X-User-ID if userEmail is provided
      const headers: Record<string, string> = {};
      if (userEmail) {
        headers["X-User-ID"] = userEmail;
      }

      const response = await this.invokeApi<UploadResponse>(config, headers);
      return new ApiResult<UploadResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getGdxDocumentDetail(
    documentId: string | number,
    flowId: string | number
  ) {
    const config = processingHubConfig.getGdxDocumentDetail(flowId, documentId);
    return withApiResponse<GdxDocumentDetail>(async () => {
      const response = await this.invokeApi<GdxDocumentDetail>(config);
      return new ApiResult<GdxDocumentDetail>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getDocumentTypeInformation(documentType: string) {
    const config = processingHubConfig.getDocumentTypeInformation(documentType);
    return withApiResponse<DocumentTypeInformation[]>(async () => {
      const response = await this.invokeApi<DocumentTypeInformation[]>(config);
      return new ApiResult<DocumentTypeInformation[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static createBatch(batchRequest: BatchCreateRequest) {
    const config = processingHubConfig.createBatch();
    return withApiResponse<BatchCreateResponse>(async () => {
      const response = await this.invokeApi<BatchCreateResponse>({
        ...config,
        data: batchRequest
      });
      return new ApiResult<BatchCreateResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getBatchesPaginated(paginationParams: {
    page: number;
    size: number;
    sort?: string;
    dir?: string;
    flow_id: number;
  }) {
    const config = processingHubConfig.getAllBatches();

    return withApiResponse<BatchPaginatedResponse>(async () => {
      const response = await this.invokeApi<BatchPaginatedResponse>({
        ...config,
        data: paginationParams
      });

      return new ApiResult<BatchPaginatedResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateGdxDocument(
    flowId: string | number,
    docId: string | number,
    updateData: any
  ) {
    const config = processingHubConfig.updateGdxDocument(flowId, docId);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>({
        ...config,
        data: updateData
      });
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }
}
